import type { CSSProperties } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useMetaTags } from '@/hooks/useMetaTags';
import { Loader2, CheckCircle, XCircle, Shield, Trash2, Share } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import PageHeader from '@/components/PageHeader';
import { isUserActive, formatLastLogin, getNetlifyFunctionsRoot } from '@/lib/utils';
import { useAlbumUsers } from '@/hooks/useAlbumUsers';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useAdminAuth, useSuperadminAuth } from '@/contexts/AdminAuthContext';
import { useTheme } from '@/contexts/ThemeProvider';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/hooks/useSettings';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';

interface UserWithRole {
  id: string;
  email: string;
  user_metadata: {
    name?: string;
    picture?: string;
    full_name?: string;
    avatar_url?: string;
  };
  last_sign_in_at: string;
  role?: string;
  is_banned: boolean;
  last_seen_at?: string;
  last_active_at?: string;
}

const Users = () => {
  const { albumId } = useAlbumContext();
  const { user: currentUser } = useAuth();
  const { isSuperadmin } = useSuperadminAuth();
  const { isAdmin } = useAdminAuth();
  const { tokens } = useTheme();
  const { users, loading, refreshUsers, activeCount, totalCount } = useAlbumUsers();
  const { getBooleanSetting } = useSettings();
  const [localUsers, setLocalUsers] = useState(users);
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});
  const [sortActiveFirst, setSortActiveFirst] = useState(false);
  const { getAlbumPath } = useAlbumNavigation();
  const cardSurface = tokens.colors.card || tokens.colors.background || '0 0% 100%';
  const cardText = tokens.colors['card-foreground'] || tokens.colors.foreground;
  const cardSurfaceStyle = useMemo<CSSProperties>(
    () => toSurfaceContrastVariables(getSurfaceContrastColors(cardSurface, cardText)) as CSSProperties,
    [cardSurface, cardText],
  );

  useMetaTags({
    title: 'Gestión de Usuarios - Admin',
    pathname: '/admin/usuarios'
  });

  // Update local users when users from hook change and sort by role
  useEffect(() => {
    let filteredUsers = [...users];

    // Filter out superadmin users for admin users if setting is false
    if (isAdmin && !isSuperadmin) {
      const showSuperadminToAdmins = getBooleanSetting('show_superadmin_to_admins', false);
      if (!showSuperadminToAdmins) {
        filteredUsers = filteredUsers.filter(user => user.role !== 'superadmin');
      }
    }

    const baseSorted = filteredUsers.sort((a, b) => {
      const roleOrder = { 'superadmin': 0, 'admin': 1, 'user': 2 };
      const roleA = roleOrder[a.role as keyof typeof roleOrder] ?? 2;
      const roleB = roleOrder[b.role as keyof typeof roleOrder] ?? 2;

      if (roleA !== roleB) {
        return roleA - roleB;
      }

      // If roles are the same, sort by last sign in (most recent first)
      const dateA = new Date(a.last_sign_in_at || 0);
      const dateB = new Date(b.last_sign_in_at || 0);
      return dateB.getTime() - dateA.getTime();
    });

    // If sorting by active first, partition preserving base order
    const finalSorted = sortActiveFirst
      ? [
        ...baseSorted.filter(u => isUserActive(u.last_active_at)),
        ...baseSorted.filter(u => !isUserActive(u.last_active_at))
      ]
      : baseSorted;

    setLocalUsers(finalSorted);

    // Only initialize toggle states for NEW users, preserve existing ones
    setToggleStates(prevToggleStates => {
      const newToggleStates = { ...prevToggleStates };
      baseSorted.forEach(user => {
        // Only set if this user doesn't already have a toggle state
        if (!(user.id in newToggleStates)) {
          newToggleStates[user.id] = user.is_banned;
        }
      });
      return newToggleStates;
    });
  }, [users, isAdmin, isSuperadmin, getBooleanSetting, sortActiveFirst]);




  const getUserName = (user: UserWithRole) => {
    return user.user_metadata?.name ||
      user.user_metadata?.full_name ||
      user.email?.split('@')[0] ||
      'Usuario';
  };

  const getUserAvatar = (user: UserWithRole) => {
    return user.user_metadata?.picture ||
      user.user_metadata?.avatar_url;
  };

  const getUserInitials = (user: UserWithRole) => {
    const name = getUserName(user);
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleBanToggle = async (userId: string, currentBanStatus: boolean) => {
    // Update toggle state immediately for instant visual feedback
    setToggleStates(prev => ({
      ...prev,
      [userId]: !currentBanStatus
    }));

    // Update local users state for row styling
    setLocalUsers(prevUsers => prevUsers.map(user =>
      user.id === userId
        ? { ...user, is_banned: !currentBanStatus }
        : user
    ));

    try {
      const { error } = await supabase
        .rpc('update_user_ban_status', {
          _user_id: userId,
          _is_banned: !currentBanStatus
        });

      if (error) throw error;

      toast({
        title: !currentBanStatus ? "Usuario bloqueado" : "Usuario desbloqueado",
        description: !currentBanStatus
          ? "El usuario ha sido bloqueado exitosamente"
          : "El usuario ha sido desbloqueado exitosamente"
      });
    } catch (error) {
      console.error('Error updating ban status:', error);

      // Revert both states on error
      setToggleStates(prev => ({
        ...prev,
        [userId]: currentBanStatus
      }));

      setLocalUsers(prevUsers => prevUsers.map(user =>
        user.id === userId
          ? { ...user, is_banned: currentBanStatus }
          : user
      ));

      toast({
        title: "Error",
        description: "No se pudo actualizar el estado del usuario",
        variant: "destructive"
      });
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .rpc('update_user_role', {
          target_user_id: userId,
          new_role: newRole,
          target_album_id: albumId
        });

      if (error) throw error;

      // Update local state immediately
      setLocalUsers(localUsers.map(user =>
        user.id === userId
          ? { ...user, role: newRole }
          : user
      ));

      toast({
        title: "Rol actualizado",
        description: `El rol del usuario ha sido cambiado a ${getRoleLabel(newRole)}`
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el rol del usuario",
        variant: "destructive"
      });
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'superadmin': return 'Superadmin';
      case 'admin': return 'Admin';
      case 'user': return 'Usuario';
      default: return 'Usuario';
    }
  };

  const getRoleVariant = (role: string) => {
    switch (role) {
      case 'superadmin': return 'border border-primary/30 bg-primary text-primary-foreground';
      case 'admin': return 'border border-secondary/30 bg-secondary text-secondary-foreground';
      case 'user': return 'border border-border bg-muted text-foreground/80';
      default: return 'border border-border bg-muted text-foreground/80';
    }
  };

  const handleUserDelete = async (userId: string, userName: string) => {
    try {
      // First sign out the user by calling the remove function
      const { error } = await supabase
        .rpc('remove_user_from_album', {
          target_user_id: userId,
          target_album_id: albumId
        });

      if (error) throw error;

      // Wait a moment to ensure database changes are propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Refresh users to get updated data from the database
      await refreshUsers();

      toast({
        title: "Usuario eliminado",
        description: `${userName} ha sido eliminado del álbum exitosamente`
      });
    } catch (error) {
      console.error('Error removing user:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el usuario del álbum",
        variant: "destructive"
      });
    }
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      if (!albumId) {
        toast({
          title: 'Error',
          description: 'No se encontró el álbum actual',
          variant: 'destructive',
        });
        return;
      }

      const params = new URLSearchParams({
        format: format === 'csv' ? 'csv' : 'xlsx',
        album_id: albumId,
      });

      const res = await fetch(`${getNetlifyFunctionsRoot()}/export-users?${params.toString()}`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Error ${res.status}`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'csv' ? 'usuarios.csv' : 'usuarios.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast({
        title: 'Exportación completa',
        description: format === 'csv' ? 'Usuarios exportados en archivo .csv' : 'Usuarios exportados en archivo .xlsx',
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      toast({
        title: 'Error al exportar',
        description: error instanceof Error ? error.message : 'No se pudo exportar los usuarios',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <PageHeader title="Panel de Anfitrión" />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb + Actions */}
        <div className="mb-6 flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={getAlbumPath("/admin")}>Panel de Anfitrión</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Usuarios</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" aria-label="Exportar">
                        <Share className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Exportar listado de usuarios</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleExport('csv'); }}>
                  Exportar como .csv
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleExport('xlsx'); }}>
                  Exportar como .xlsx
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">Cargando usuarios...</p>
            </div>
          </div>
        ) : (
          <div
            className="bg-card rounded-lg border shadow-sm text-[hsl(var(--surface-fg))]"
            style={cardSurfaceStyle}
          >
            <div className="p-6 border-b">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-[hsl(var(--surface-fg))]">Usuarios</h2>
                  <p className="mt-1 text-sm text-[hsl(var(--surface-muted-fg))]">
                    Gestión de sesiones y roles del sistema
                  </p>
                </div>
                <div className="flex items-center justify-center md:justify-end gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[hsl(var(--surface-fg))]">
                      {totalCount}
                    </div>
                    <div className="text-xs text-[hsl(var(--surface-muted-fg))]">
                      Registrado{(totalCount > 1 || totalCount == 0) && "s"}
                    </div>
                  </div>
                  <div
                    className="text-center cursor-pointer select-none hover:opacity-90"
                    title="Ordenar por activos"
                    onClick={() => setSortActiveFirst(true)}
                  >
                    <div className="text-3xl font-bold" data-number={activeCount}>
                      {activeCount}
                    </div>
                    <div className="text-xs text-[hsl(var(--surface-muted-fg))]">
                      Activo{(activeCount > 1 || activeCount == 0) && "s"} ahora
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Última actividad</TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="text-left hover:underline cursor-pointer"
                        title="Ordenar por activos"
                        onClick={() => setSortActiveFirst(true)}
                      >
                        Activo
                      </button>
                    </TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>
                      <div className="flex items-center space-x-2">
                        <span>Bloquear</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                                <Shield className="w-4 h-4 text-[hsl(var(--surface-muted-fg))] cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>El usuario no podrá acceder al sistema y sus fotos se ocultarán hasta ser desbloqueado.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableHead>
                    {isSuperadmin && (
                      <TableHead>
                        <div className="flex items-center space-x-2">
                          <span>Eliminar</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Trash2 className="w-4 h-4 text-[hsl(var(--surface-muted-fg))] cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Eliminar el registro del usuario. Nota: el usuario puede volver a registrarse.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {localUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className={user.is_banned ? "bg-muted/50" : ""}
                    >
                      <TableCell>
                        <Link
                          to={getAlbumPath(`/usuario?userId=${user.id}`)}
                          className="flex items-center space-x-3 hover:opacity-75 transition-opacity"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={getUserAvatar(user)} />
                            <AvatarFallback className="text-xs">
                              {getUserInitials(user)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium">{getUserName(user)}</span>
                            <span className="text-xs text-[hsl(var(--surface-muted-fg))]">{user.email}</span>
                          </div>
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs text-[hsl(var(--surface-muted-fg))]">
                        {formatLastLogin(user.last_active_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {isUserActive(user.last_active_at) ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className={`text-xs font-medium ${isUserActive(user.last_active_at) ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                            }`}>
                            {isUserActive(user.last_active_at) ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isSuperadmin && user.id !== currentUser?.id ? (
                          <Select value={user.role || 'user'} onValueChange={(newRole) => handleRoleChange(user.id, newRole)}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Usuario</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="superadmin">Superadmin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleVariant(user.role || 'user')}`}>
                            {getRoleLabel(user.role || 'user')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.role !== 'admin' && user.role !== 'superadmin' ? (
                          <Switch
                            checked={toggleStates[user.id] ?? user.is_banned}
                            onCheckedChange={() => handleBanToggle(user.id, toggleStates[user.id] ?? user.is_banned)}
                            className="data-[state=checked]:bg-red-600 data-[state=unchecked]:bg-slate-400 dark:data-[state=unchecked]:bg-slate-600"
                          />
                        ) : (
                          <span className="text-sm text-[hsl(var(--surface-muted-fg))]">-</span>
                        )}
                      </TableCell>
                      {isSuperadmin && (
                        <TableCell>
                          {user.role === 'user' ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta acción eliminará a {getUserName(user)} del álbum permanentemente.
                                    Esta acción no se puede deshacer.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleUserDelete(user.id, getUserName(user))}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <span className="text-sm text-[hsl(var(--surface-muted-fg))]">-</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4 p-4">
              {localUsers.map((user) => (
                <div
                  key={user.id}
                  className={`p-4 rounded-lg border transition-colors ${user.is_banned
                    ? "bg-muted/50 border-muted"
                    : "bg-card border-border"
                    }`}
                >
                  <div className="flex justify-between ">
                    <div className="flex flex-col">
                      <div className="flex items-start space-x-4 mb-3">
                        {/* User Avatar and Info */}
                        <Link
                          to={getAlbumPath(`/usuario?userId=${user.id}`)}
                          className="flex items-start space-x-3 hover:opacity-75 transition-opacity flex-1"
                        >
                          <Avatar className="h-14 w-14 flex-shrink-0">
                            <AvatarImage src={getUserAvatar(user)} />
                            <AvatarFallback className="text-xs">
                              {getUserInitials(user)}
                            </AvatarFallback>
                          </Avatar>

                        </Link>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium">{getUserName(user)}</span>
                        <span className="text-xs text-[hsl(var(--surface-muted-fg))]">{user.email}</span>
                      </div>

                      <div className="mt-7">
                        {isSuperadmin && user.id !== currentUser?.id ? (
                          <Select value={user.role || 'user'} onValueChange={(newRole) => handleRoleChange(user.id, newRole)}>
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Usuario</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="superadmin">Superadmin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleVariant(user.role || 'user')}`}>
                            {getRoleLabel(user.role || 'user')}
                          </span>
                        )}
                      </div>


                    </div>


                    {/* Actions */}
                    <div className="mt-3 flex flex-col items-end">


                      {/* Floating Status */}
                      <div className="flex flex-col items-end space-x-2">
                        {isUserActive(user.last_active_at) ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span className={`text-xs font-medium ${isUserActive(user.last_active_at) ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                          }`}>
                          {isUserActive(user.last_active_at) ? 'Activo' : 'Inactivo'}
                        </span>
                        <span className="mt-1 mb-6 text-xs text-[hsl(var(--surface-muted-fg))]">
                          {formatLastLogin(user.last_active_at)}
                        </span>
                      </div>


                      <div className="flex items-center space-x-2 mb-4">
                        {user.role !== 'admin' && user.role !== 'superadmin' ? (
                          <>
                            <span className="text-sm text-[hsl(var(--surface-muted-fg))]">Bloquear:</span>
                            <Switch
                              checked={toggleStates[user.id] ?? user.is_banned}
                              onCheckedChange={() => handleBanToggle(user.id, toggleStates[user.id] ?? user.is_banned)}
                              className="data-[state=checked]:bg-red-600 data-[state=unchecked]:bg-slate-400 dark:data-[state=unchecked]:bg-slate-600"
                            />
                          </>
                        ) : (
                          <span className="text-sm text-[hsl(var(--surface-muted-fg))]"></span>
                        )}
                      </div>

                      {isSuperadmin && user.role === 'user' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Eliminar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción eliminará a {getUserName(user)} del álbum permanentemente.
                                Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleUserDelete(user.id, getUserName(user))}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>


                </div>
              ))}
            </div>

            {localUsers.length === 0 && (
              <div className="p-6 text-center text-[hsl(var(--surface-muted-fg))]">
                No se encontraron usuarios
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
